<script setup lang="ts">
import { faCalendarLines } from '@fortawesome/pro-solid-svg-icons';
import { type DayPrice } from './types';

const { item, allIsLoading } = defineProps<{
    item: DayPrice;
    allIsLoading: boolean;
}>();
const emit = defineEmits<{
    click: [day: Date];
}>();
const dateStr = computed(() => dayConfig.js(item.date).format('dddd MM/D'));
const priceStr = computed(() => {
    if (item.isAvailable) {
        return `${formatMoney(item.price || 0, {
            unit: 'KToman',
        })} تومانء`;
    } else {
        return '-';
    }
});
const priceClass = computed(() => {
    if (!item.isAvailable) {
        return '';
    }
    if (item.isMinPrice) {
        return 'min-price';
    } else if (item.isMaxPrice) {
        return 'max-price';
    }
    return '';
});
const onClick = () => {
    if (item.isSelected || item.disabled) {
        return;
    }
    emit('click', item.date);
};
</script>

<template>
    <div
        class="day-view-container"
        :class="{
            selected: item.isSelected,
            disabled: item.disabled,
        }"
        @click="onClick"
    >
        <mr-typography
            size="small"
            color="var(--gray-700)"
            white-space="nowrap"
            :class="{ 'loading-skeleton': allIsLoading }"
        >
            {{ dateStr }}
        </mr-typography>
        <div
            class="price-container"
            :class="{
                loading: allIsLoading || item.isLoading,
            }"
        >
            <span class="loading-skeleton" />
            <mr-typography
                class="price"
                :class="[priceClass]"
                white-space="nowrap"
            >
                <span v-if="priceStr !== '-'"> {{ priceStr }}</span>
                <mr-icon
                    v-else
                    :icon="faCalendarLines"
                    color="var(--blue-200)"
                />
            </mr-typography>
        </div>
        <span class="border" />
    </div>
</template>

<style lang="scss" scoped>
.day-view-container {
    position: relative;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    width: 88px;
    padding: 0 4px;
    margin: 0 6px;
    cursor: pointer;
    user-select: none;
    border-radius: 8px;
    @include mobile-only {
        width: unset;
        min-width: 60px;
        padding: 0 4px;
    }

    &.selected {
        .border {
            opacity: 1;
        }
    }
    &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .border {
        position: absolute;
        height: 4px;
        left: 0;
        right: 0;
        bottom: -8px;
        background: usePalette();
        border-radius: 4px 4px 0 0;
        opacity: 0;
        transition: opacity 200ms linear;

        @include mobile-only {
            bottom: -12px;
        }
    }
}

.price-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -6px;
    width: 100%;
    &.loading {
        .price {
            opacity: 0;
        }
        .loading-skeleton {
            opacity: 1;
            display: block;
        }
    }
    .loading-skeleton {
        position: absolute;
        width: 40px;
        height: 16px;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 2;
        opacity: 0;
        transition: all 400ms;
        border-radius: 20px;
        display: none;
    }
    .price {
        display: flex;
        align-items: center;
        opacity: 1;
        transition: all 400ms;
        height: 32px;
        // text
        @include tg-l($w7, usePalette($p-gray, $p-800));
        @include mobile-only {
            @include tg-m($w7, usePalette($p-gray, $p-800));
        }
        &.min-price {
            color: usePalette($p-green, $p-500);
        }
    }
}
</style>
